/**
 * Devices View — Paired device management with pairing wizard.
 *
 * Three states:
 *   1. Device list (default) — shows paired devices with online status
 *   2. Pairing wizard — displays code for companion app entry
 *   3. Success screen — confirms successful pairing
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { getServices, type DeviceInfo } from "../services/index.ts";

const DEVICE_ICONS: Record<string, string> = {
  macos: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
  ios: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>`,
  android: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>`,
  linux: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
  windows: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
};

/** Download page for the companion app. */
const COMPANION_DOWNLOAD_URL = "/#download";

const CAPABILITY_LABELS: Record<string, string> = {
  browser: "Browser control",
  bash: "Terminal access",
  filesystem: "File system",
};

type ViewState =
  | { kind: "list" }
  | { kind: "pairing"; code: string; expiresAt: string }
  | { kind: "success"; deviceName: string };

@customElement("katanoid-devices-view")
export class KatanoidDevicesView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private devices: DeviceInfo[] = [];
  @state() private loading = true;
  @state() private view: ViewState = { kind: "list" };
  @state() private pairingError: string | null = null;
  @state() private pairingTimeLeft = 0;

  private pairingPollTimer: ReturnType<typeof setInterval> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadDevices();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopPairingPoll();
    this.stopCountdown();
  }

  private async loadDevices(): Promise<void> {
    this.loading = true;
    try {
      const { devices } = getServices();
      this.devices = await devices.listDevices();
    } catch {
      this.devices = [];
    }
    this.loading = false;
  }

  protected override render(): TemplateResult {
    if (this.view.kind === "pairing") return this.renderPairingWizard(this.view);
    if (this.view.kind === "success") return this.renderSuccess(this.view);
    return this.renderList();
  }

  // ── List View ──

  private renderList(): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Devices</h1>
          <button class="btn btn--primary btn--sm" @click=${this.handlePairNew}>
            + Pair Device
          </button>
        </div>

        <div class="device-info-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <span>
            Paired devices let your assistant control apps, run commands,
            and access files on your computer \u2014 all with your permission.
          </span>
        </div>

        ${this.loading
          ? html`
              <div class="devices-list">
                <div class="shimmer shimmer--card"></div>
                <div class="shimmer shimmer--card"></div>
              </div>
            `
          : this.devices.length === 0
            ? this.renderEmptyState()
            : this.renderDeviceList()}
      </div>
    `;
  }

  private renderEmptyState(): TemplateResult {
    return html`
      <div class="sessions-empty">
        <div class="sessions-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        </div>
        <h2 class="sessions-empty-title">No paired devices</h2>
        <p class="sessions-empty-desc">
          Pair a device to let your assistant control your
          computer, browse the web, and manage files on your behalf.
        </p>
        <div class="sessions-empty-actions">
          <button class="btn btn--primary" @click=${this.handlePairNew}>
            Pair your first device
          </button>
          <a
            href="${COMPANION_DOWNLOAD_URL}"
            target="_blank"
            class="btn btn--ghost btn--sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download companion app
          </a>
        </div>
      </div>
    `;
  }

  private renderDeviceList(): TemplateResult {
    return html`
      <div class="devices-list stagger-children">
        ${this.devices.map((d) => this.renderDeviceCard(d))}
      </div>

      <div class="devices-download-banner">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        <a href="${COMPANION_DOWNLOAD_URL}" target="_blank" class="devices-download-link">
          Download companion app for another device
        </a>
      </div>
    `;
  }

  private renderDeviceCard(d: DeviceInfo): TemplateResult {
    const icon = DEVICE_ICONS[d.type] ?? DEVICE_ICONS.macos;
    const lastSeen = d.lastSeenAt ? this.formatLastSeen(d.lastSeenAt) : "Never";
    const isOnline = d.status === "online";
    const capabilities = Object.entries(d.capabilities).filter(([, v]) => v);

    return html`
      <div class="device-card">
        <div class="device-card-header">
          <div class="device-card-icon" .innerHTML=${icon}></div>
          <div class="device-card-info">
            <div class="device-card-name">${d.name}</div>
            <div class="device-card-status">
              <span class="status-dot ${isOnline ? "status-dot--connected" : "status-dot--disconnected"}"></span>
              ${isOnline ? "Online" : `Last seen ${lastSeen}`}
            </div>
          </div>
        </div>

        ${capabilities.length > 0
          ? html`
              <div class="device-capabilities">
                ${capabilities.map(
                  ([cap]) => html`
                    <span class="device-capability-chip">
                      ${CAPABILITY_LABELS[cap] ?? cap}
                    </span>
                  `
                )}
              </div>
            `
          : nothing}

        <div class="device-card-footer">
          <span class="device-card-paired">Paired ${this.formatPairedDate(d.pairedAt)}</span>
          <button
            class="btn btn--ghost btn--sm session-card-delete"
            @click=${() => this.handleRemove(d.id)}
          >
            Remove
          </button>
        </div>
      </div>
    `;
  }

  // ── Pairing Wizard ──

  private renderPairingWizard(v: { code: string; expiresAt: string }): TemplateResult {
    const minutes = Math.floor(this.pairingTimeLeft / 60);
    const seconds = this.pairingTimeLeft % 60;
    const expired = this.pairingTimeLeft <= 0;

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button class="btn btn--ghost btn--sm" @click=${this.handleCancelPairing}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
        </div>

        <div class="pairing-wizard">
          <div class="pairing-wizard-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </div>

          <h2 class="pairing-wizard-title">Pair a device</h2>
          <p class="pairing-wizard-desc">
            Open the Katanoid companion app on your computer and enter this code.
          </p>

          <div class="pairing-code ${expired ? "pairing-code--expired" : ""}">
            ${v.code.split("").map((c) => html`<span class="pairing-code-char">${c}</span>`)}
          </div>

          ${expired
            ? html`
                <p class="pairing-wizard-expired">Code expired</p>
                <button class="btn btn--primary btn--sm" @click=${this.handlePairNew}>
                  Generate new code
                </button>
              `
            : html`
                <p class="pairing-wizard-timer">
                  Expires in ${minutes}:${seconds.toString().padStart(2, "0")}
                </p>
              `}

          ${this.pairingError
            ? html`<p class="pairing-wizard-error">${this.pairingError}</p>`
            : nothing}

          <div class="pairing-wizard-steps">
            <div class="pairing-step">
              <span class="pairing-step-num">1</span>
              <span><a href="${COMPANION_DOWNLOAD_URL}" target="_blank" class="pairing-step-link">Download the companion app</a> for your computer</span>
            </div>
            <div class="pairing-step">
              <span class="pairing-step-num">2</span>
              <span>Open the app and click "Pair"</span>
            </div>
            <div class="pairing-step">
              <span class="pairing-step-num">3</span>
              <span>Enter the code shown above</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ── Success Screen ──

  private renderSuccess(v: { deviceName: string }): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="pairing-wizard">
          <div class="pairing-success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>

          <h2 class="pairing-wizard-title">Device paired</h2>
          <p class="pairing-wizard-desc">
            <strong>${v.deviceName}</strong> is now connected.
            Your assistant can now control your browser, run commands, and access files
            \u2014 with your approval for each action.
          </p>

          <button class="btn btn--primary" @click=${this.handleDone}>
            Done
          </button>
        </div>
      </div>
    `;
  }

  // ── Handlers ──

  private handlePairNew = async (): Promise<void> => {
    this.pairingError = null;
    try {
      const { devices } = getServices();
      const { code, expiresAt } = await devices.generatePairingCode();
      this.view = { kind: "pairing", code, expiresAt };
      this.startPairingPoll(code);
      this.startCountdown(expiresAt);
    } catch (err) {
      this.pairingError = (err as Error).message ?? "Failed to generate pairing code";
    }
  };

  private handleCancelPairing = (): void => {
    this.stopPairingPoll();
    this.stopCountdown();
    this.view = { kind: "list" };
  };

  private handleDone = (): void => {
    this.view = { kind: "list" };
    this.loadDevices();
  };

  private handleRemove = async (id: string): Promise<void> => {
    try {
      const { devices } = getServices();
      await devices.removeDevice(id);
      this.devices = this.devices.filter((d) => d.id !== id);
    } catch { /* ignore */ }
  };

  // ── Pairing Polling ──

  private startPairingPoll(code: string): void {
    this.stopPairingPoll();
    this.pairingPollTimer = setInterval(async () => {
      try {
        const { devices } = getServices();
        const status = await devices.getPairingStatus(code);
        if (status.used) {
          this.stopPairingPoll();
          this.stopCountdown();
          this.view = { kind: "success", deviceName: status.deviceName ?? "Device" };
          this.loadDevices();
        }
      } catch { /* ignore polling errors */ }
    }, 2000);
  }

  private stopPairingPoll(): void {
    if (this.pairingPollTimer) {
      clearInterval(this.pairingPollTimer);
      this.pairingPollTimer = null;
    }
  }

  // ── Countdown Timer ──

  private startCountdown(expiresAt: string): void {
    this.stopCountdown();
    const expiresMs = new Date(expiresAt).getTime();

    const tick = () => {
      const left = Math.max(0, Math.floor((expiresMs - Date.now()) / 1000));
      this.pairingTimeLeft = left;
      if (left <= 0) {
        this.stopCountdown();
        this.stopPairingPoll();
      }
    };

    tick();
    this.countdownTimer = setInterval(tick, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  // ── Helpers ──

  private formatLastSeen(iso: string): string {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  }

  private formatPairedDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-devices-view": KatanoidDevicesView;
  }
}
